import { extractTextFromPdfFile, isPdfFile } from './pdfExtractor';
import { extractTextFromDocFile, isDocFile } from './docExtractor';
import { extractFromWebUrl, isWebUrl } from './webExtractor';

const YOUTUBE_HOSTS = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'www.youtu.be'];
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.m4a', '.ogg', '.flac', '.aac', '.webm'];

export const isAudioFile = (file) => {
	if (!file) return false;
	const byMime = (file.type || '').toLowerCase().startsWith('audio/');
	const lowerName = (file.name || '').toLowerCase();
	const byExt = AUDIO_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
	return byMime || byExt;
};

const formatDuration = (seconds) => {
	if (!Number.isFinite(seconds) || seconds <= 0) return null;
	const total = Math.round(seconds);
	const minutes = Math.floor(total / 60);
	const secs = String(total % 60).padStart(2, '0');
	return `${minutes}:${secs}`;
};

const readAudioMetadata = (file) => new Promise((resolve) => {
	if (typeof document === 'undefined' || typeof URL === 'undefined') {
		resolve({ durationSeconds: null, durationLabel: null });
		return;
	}

	const audio = document.createElement('audio');
	audio.preload = 'metadata';

	const objectUrl = URL.createObjectURL(file);
	let settled = false;
	let timeoutId = null;

	const settle = (value) => {
		if (settled) return;
		settled = true;
		if (timeoutId) clearTimeout(timeoutId);
		audio.removeEventListener('loadedmetadata', onLoaded);
		audio.removeEventListener('error', onError);
		audio.removeAttribute('src');
		audio.load();
		URL.revokeObjectURL(objectUrl);
		resolve(value);
	};

	const onLoaded = () => {
		const durationSeconds = Number.isFinite(audio.duration) ? audio.duration : null;
		settle({
			durationSeconds,
			durationLabel: formatDuration(durationSeconds),
		});
	};

	const onError = () => {
		settle({ durationSeconds: null, durationLabel: null });
	};

	timeoutId = setTimeout(() => {
		settle({ durationSeconds: null, durationLabel: null });
	}, 3500);

	audio.addEventListener('loadedmetadata', onLoaded, { once: true });
	audio.addEventListener('error', onError, { once: true });
	audio.src = objectUrl;
});

export const extractAudioMetadata = async (file, options = {}) => {
	if (!isAudioFile(file)) {
		throw new Error('Le fichier fourni n\'est pas un audio valide.');
	}

	const audioInfo = await readAudioMetadata(file);
	const formData = new FormData();
	formData.append('audio', file, file.name);
	formData.append('fileName', file.name);
	formData.append('mimeType', file.type || 'audio/unknown');
	formData.append('size', String(file.size));
	formData.append('lastModified', String(file.lastModified || ''));
	formData.append('durationSeconds', String(audioInfo.durationSeconds || ''));
	formData.append('durationLabel', audioInfo.durationLabel || '');
	formData.append('language', options.language || 'fr');
	if (options.model) {
		formData.append('model', options.model);
	}

	const response = await fetch(`${BACKEND_BASE_URL}/api/ai/audio/extract`, {
		method: 'POST',
		body: formData,
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(errorData?.error || `Echec extraction audio (${response.status})`);
	}

	const data = await response.json();
	const content = (data?.content || '').trim();
	const durationLabel = data?.metadata?.duration || audioInfo.durationLabel;
	const lines = [
		`Nom: ${file.name}`,
		`Type: ${file.type || 'audio/unknown'}`,
		`Taille: ${(file.size / (1024 * 1024)).toFixed(2)} MB`,
		durationLabel ? `Duree: ${durationLabel}` : 'Duree: non detectee',
		'Transcription:',
		content || 'no text detected.',
	];

	return {
		type: 'audio',
		fileName: file.name,
		mimeType: file.type || 'audio/unknown',
		size: file.size,
		lastModified: file.lastModified,
		durationSeconds: data?.metadata?.durationSeconds ?? audioInfo.durationSeconds,
		durationLabel,
		text: lines.join('\n'),
	};
};

const normalizeUrl = (input) => {
	if (!input || typeof input !== 'string') {
		throw new Error('Lien YouTube invalide.');
	}

	const raw = input.trim();
	if (!raw) {
		throw new Error('Lien YouTube vide.');
	}

	try {
		return new URL(raw);
	} catch (error) {
		return new URL(`https://${raw}`);
	}
};

export const isYoutubeUrl = (input) => {
	try {
		const url = normalizeUrl(input);
		return YOUTUBE_HOSTS.includes(url.hostname.toLowerCase());
	} catch (error) {
		return false;
	}
};

export const extractYoutubeVideoId = (input) => {
	if (!isYoutubeUrl(input)) return null;

	const url = normalizeUrl(input);
	const host = url.hostname.toLowerCase();

	if (host.includes('youtu.be')) {
		const id = url.pathname.replace(/^\//, '').split('/')[0];
		return id || null;
	}

	const byQuery = url.searchParams.get('v');
	if (byQuery) return byQuery;

	const pathParts = url.pathname.split('/').filter(Boolean);
	const shortsIndex = pathParts.indexOf('shorts');
	if (shortsIndex >= 0 && pathParts[shortsIndex + 1]) {
		return pathParts[shortsIndex + 1];
	}

	const embedIndex = pathParts.indexOf('embed');
	if (embedIndex >= 0 && pathParts[embedIndex + 1]) {
		return pathParts[embedIndex + 1];
	}

	return null;
};

const normalizeTranscriptText = (value) => (value || '')
	.replace(/\s+/g, ' ')
	.trim();

const extractYoutubeViaProgressStream = async (url, onProgress) => {
	if (typeof EventSource === 'undefined') {
		return null;
	}

	return new Promise((resolve, reject) => {
		let settled = false;
		const streamUrl = `${BACKEND_BASE_URL}/api/youtube/extract/stream?videoUrl=${encodeURIComponent(url)}`;
		const eventSource = new EventSource(streamUrl);

		const cleanup = () => {
			eventSource.close();
		};

		eventSource.addEventListener('progress', (event) => {
			if (typeof onProgress !== 'function') return;
			try {
				const payload = JSON.parse(event.data || '{}');
				onProgress(payload);
			} catch (error) {
				// Ignore malformed progress events.
			}
		});

		eventSource.addEventListener('done', (event) => {
			if (settled) return;
			settled = true;
			cleanup();

			try {
				const payload = JSON.parse(event.data || '{}');
				resolve(payload?.data || null);
			} catch (error) {
				reject(error);
			}
		});

		eventSource.addEventListener('error', (event) => {
			if (settled) return;
			settled = true;
			cleanup();

			try {
				const payload = JSON.parse(event?.data || '{}');
				reject(new Error(payload?.error || 'Echec extraction YouTube en streaming'));
			} catch (error) {
				reject(new Error('Echec extraction YouTube en streaming'));
			}
		});

		eventSource.onerror = () => {
			if (settled) return;
			settled = true;
			cleanup();
			reject(new Error('Connexion de progression YouTube interrompue'));
		};
	});
};

const buildYoutubeText = ({ title, authorName, url, videoId, thumbnailUrl, transcript, failureReason }) => {
	const lines = [
		'Source: YouTube',
		`Titre: ${title || 'inconnu'}`,
		`Auteur: ${authorName || 'inconnu'}`,
		`Video ID: ${videoId || 'inconnu'}`,
		`URL: ${url}`,
	];

	if (thumbnailUrl) lines.push(`Miniature: ${thumbnailUrl}`);

	if (transcript) {
		lines.push('Transcript:');
		lines.push(transcript);
	} else {
		const reason = String(failureReason || '').trim();
		lines.push(`Transcript: indisponible (sous-titres non disponibles et transcription audio impossible ou en echec${reason ? `: ${reason}` : ''}).`);
	}

	return lines.join('\n');
};

export const extractFromYoutubeUrl = async (input, options = {}) => {
	if (!isYoutubeUrl(input)) {
		throw new Error('Le lien fourni n\'est pas un lien YouTube valide.');
	}

	const url = normalizeUrl(input).toString();
	const videoId = extractYoutubeVideoId(url);
	const { maxTranscriptChars = 180000, onProgress } = options;

	let title = '';
	let authorName = '';
	let thumbnailUrl = '';
	let transcript = '';
	let failureReason = '';
	let backendText = '';
	let streamedData = null;

	try {
		if (typeof onProgress === 'function') {
			streamedData = await extractYoutubeViaProgressStream(url, onProgress);
		}

		if (streamedData) {
			title = streamedData?.title || '';
			authorName = streamedData?.authorName || '';
			thumbnailUrl = streamedData?.thumbnailUrl || '';
			transcript = normalizeTranscriptText(streamedData?.transcript || '');
			failureReason = String(streamedData?.failureReason || '').trim();
			backendText = String(streamedData?.text || '').trim();
		} else {
			const response = await fetch(`${BACKEND_BASE_URL}/api/youtube/extract`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ videoUrl: url }),
			});

			if (response.ok) {
				const data = await response.json();
				title = data?.data?.title || '';
				authorName = data?.data?.authorName || '';
				thumbnailUrl = data?.data?.thumbnailUrl || '';
				transcript = normalizeTranscriptText(data?.data?.transcript || '');
				failureReason = String(data?.data?.failureReason || '').trim();
				backendText = String(data?.data?.text || '').trim();
			}
		}
	} catch (error) {
		failureReason = String(error?.message || '').trim();
		backendText = '';
	}

	if (transcript.length > maxTranscriptChars) {
		transcript = transcript.slice(0, maxTranscriptChars);
	}

	if (!title && typeof fetch !== 'undefined') {
		try {
			const response = await fetch(`${BACKEND_BASE_URL}/api/youtube/oembed?url=${encodeURIComponent(url)}`, { method: 'GET' });
			if (response.ok) {
				const payload = await response.json();
				title = payload?.data?.title || '';
				authorName = payload?.data?.author_name || authorName;
				thumbnailUrl = payload?.data?.thumbnail_url || thumbnailUrl;
			}
		} catch (error) {
			// Keep graceful fallback.
		}
	}

	const computedText = backendText || buildYoutubeText({
		title,
		authorName,
		url,
		videoId,
		thumbnailUrl,
		transcript,
		failureReason,
	});

	return {
		type: 'youtube',
		sourceType: 'youtube',
		url,
		videoId,
		title,
		authorName,
		thumbnailUrl,
		transcript,
		failureReason,
		text: computedText,
	};
};

const isFileLike = (value) => {
	if (!value || typeof value !== 'object') return false;
	if (typeof File !== 'undefined' && value instanceof File) return true;
	return typeof value.name === 'string' && typeof value.size === 'number';
};

const resolveResource = (resource) => {
	if (!resource) return { kind: 'unknown', value: resource };

	if (typeof resource === 'string') {
		const raw = resource.trim();
		if (!raw) return { kind: 'unknown', value: raw };
		if (isYoutubeUrl(raw)) return { kind: 'youtube', value: raw };
		if (isWebUrl(raw)) return { kind: 'web', value: raw };
		return { kind: 'unknown', value: raw };
	}

	if (isFileLike(resource)) {
		if (isAudioFile(resource)) return { kind: 'audio', value: resource };
		if (isPdfFile(resource)) return { kind: 'pdf', value: resource };
		if (isDocFile(resource)) return { kind: 'doc', value: resource };
		return { kind: 'unknown-file', value: resource };
	}

	if (typeof resource === 'object') {
		if (resource.file && isFileLike(resource.file)) {
			return resolveResource(resource.file);
		}

		if (typeof resource.url === 'string') {
			return resolveResource(resource.url);
		}

		if (typeof resource.link === 'string') {
			return resolveResource(resource.link);
		}

		if (typeof resource.type === 'string') {
			const type = resource.type.toLowerCase();
			if (type === 'youtube') return { kind: 'youtube', value: resource.url || resource.link || '' };
			if (type === 'web') return { kind: 'web', value: resource.url || resource.link || '' };
			if (type === 'audio' && isFileLike(resource.file)) return { kind: 'audio', value: resource.file };
			if (type === 'pdf' && isFileLike(resource.file)) return { kind: 'pdf', value: resource.file };
			if (type === 'doc' && isFileLike(resource.file)) return { kind: 'doc', value: resource.file };
		}
	}

	return { kind: 'unknown', value: resource };
};

export const getExtractorForResource = (resource) => {
	const resolved = resolveResource(resource);

	switch (resolved.kind) {
		case 'audio':
			return extractAudioMetadata;
		case 'pdf':
			return extractTextFromPdfFile;
		case 'doc':
			return extractTextFromDocFile;
		case 'youtube':
			return extractFromYoutubeUrl;
		case 'web':
			return extractFromWebUrl;
		default:
			return null;
	}
};

export const extractFromResource = async (resource, options = {}) => {
	const resolved = resolveResource(resource);
	const extractor = getExtractorForResource(resource);

	if (!extractor) {
		throw new Error('Type de ressource non pris en charge pour extraction.');
	}

	return extractor(resolved.value, options);
};

export const detectResourceKind = (resource) => resolveResource(resource).kind;

export {
	extractTextFromPdfFile,
	extractTextFromDocFile,
	extractFromWebUrl,
	isPdfFile,
	isDocFile,
	isWebUrl,
};
